import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Seo } from "../components/Seo";

type Content = {
  settings: {
    contact?: { email?: string; mobile?: string; pickupAddress?: string };
    policies?: { payment?: string; variation?: string };
  };
};

const LAST_UPDATED = "August 7, 2026";

export function TermsOfUsePage() {
  const content = useQuery({ queryKey: ["content"], queryFn: () => api<Content>("/content") });
  const contact = content.data?.settings.contact;
  const policies = content.data?.settings.policies;
  return <section className="container-page py-10 md:py-16">
    <Seo title="Terms of Use" description="The terms that apply when you order from Minis by Cass." path="/terms-of-use" />
    <div className="mx-auto max-w-3xl">
      <h1 className="heading">Terms of Use</h1>
      <p className="mt-4 text-sm text-muted">Last updated: {LAST_UPDATED}</p>
      <div className="prose-legal mt-10 grid max-w-[68ch] gap-8 leading-relaxed text-cocoa/80 [&_a]:font-bold [&_a]:text-cocoa [&_a]:underline [&_a]:decoration-cocoa/30 [&_a]:underline-offset-4">
        <p>These terms apply whenever you browse or order from Minis by Cass. By placing an order, you agree to them. Please read them alongside our <Link to="/privacy-policy" className="font-bold text-clay-dark underline">Privacy Policy</Link>.</p>

        <Section title="Ordering as a guest">
          <p>You don't need to create an account to order — checkout is guest-only. Before an order is created, we verify the email you provide with a one-time code, to keep the production queue free of spam and fake orders.</p>
        </Section>

        <Section title="Handmade products">
          <p>{policies?.variation ?? "Handmade items can have tiny variations — that is part of their charm."} Photos are a close representation, not an exact guarantee, of the finished piece.</p>
        </Section>

        <Section title="Payment and production">
          <p>{policies?.payment ?? "Orders join the production queue only after payment is verified."} After you place an order, you'll be asked to pay and upload your proof of payment. Your order is held for a limited time to allow payment; if no proof is submitted before that window closes, the order is automatically cancelled and any reserved stock is released. If a submitted proof is unclear or doesn't match, we may ask you to resubmit it before your order can proceed.</p>
          <p>The total shown when your order is created is the final, frozen amount for that order — it will not change afterward even if prices are later updated for future orders.</p>
        </Section>

        <Section title="Pricing and currency">
          <p>All prices are listed in Philippine Peso (₱). We may update prices at any time; changes only apply to orders placed after the update.</p>
        </Section>

        <Section title="Shipping and pickup">
          <p>Choose pickup or delivery at checkout. Estimated making and delivery times shown on each product are estimates, not guarantees, and can vary — especially for made-to-order and customized pieces.</p>
        </Section>

        <Section title="Cancellations and changes">
          <p>Contact us as soon as possible if you need to change or cancel an order. Once a piece has entered production, changes or cancellations may no longer be possible. Unpaid orders are cancelled automatically as described above.</p>
        </Section>

        <Section title="Intellectual property">
          <p>Product photos, designs, and site content belong to Minis by Cass unless stated otherwise. Reference photos you upload for a customized piece remain yours, and are used only to make your order.</p>
        </Section>

        <Section title="Limitation of liability">
          <p>We do our best to describe and picture every product accurately and to fulfill every order carefully. To the extent permitted by law, Minis by Cass is not liable for indirect or incidental damages arising from your use of the site or your order, beyond the value of the order itself.</p>
        </Section>

        <Section title="Governing law">
          <p>These terms are governed by the laws of the Republic of the Philippines.</p>
        </Section>

        <Section title="Changes to these terms">
          <p>We may update these terms as the shop grows. The “Last updated” date at the top of this page will always reflect the latest version.</p>
        </Section>

        <Section title="Contact us">
          <p>Questions about these terms? Reach out to us{contact?.email ? <> at <a className="font-bold text-clay-dark underline" href={`mailto:${contact.email}`}>{contact.email}</a></> : null}{contact?.mobile ? <> or <span className="font-bold text-ink">{contact.mobile}</span></> : null}, or use the help button on the site.</p>
        </Section>
      </div>
      <Link to="/" className="btn-secondary mt-10 inline-flex">Back to the shop</Link>
    </div>
  </section>;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return <div><h2 className="text-xl font-bold text-cocoa">{title}</h2><div className="mt-3 grid gap-3 leading-relaxed">{children}</div></div>;
}
